import { createStore } from 'vuex'
import dataPersistAndSync from '@/js/dataPersistAndSync.js'

const store = createStore({
	strict: process.env.NODE_ENV !== 'production',
	state: {
		activeDeck: null,
		analyzerFilter: {
			category: null,
			attribute: null,
		},
		appUpdateCheckingIsPaused: false,
		decks: [],
		dialogData: {},
		focusCardBar: '',
		highlightedCardLIIndex: -1,
		holdingShiftKeyInEditor: false,
		idOfShowingDialog: null,
		listEntryData: {},
		loadDefaultDecks: true, // `true` means the sample decks will need to be loaded; `false` means those decks were already loaded.
		movedAllGroupActionCount: 0,
		numUserVisitsToDonatePage: 0,
		overlayHoverEnabled: false,
		permanentlyHiddenNotifications: [],
		showingAnyPopup: false,
		showCard: false,
		showDeckMenu: false,
		showSideboard: false,
		temporarilyHiddenNotifications: [],
		timeAppLastModified: 0,
		updateJustInstalled: undefined,
		viewedDrawnCardName: '',
		waitingToApplyUpdate: false,
	},
	getters: {},
	mutations: {
		activeDeck(state, payload) {
			state.activeDeck = payload
		},
		addNewCardToGroup(state, { group, card }) {
			if (!group && !card) return

			group.cards.push(card)
		},
		analyzerFilter(state, payload) {
			state.analyzerFilter = payload
		},
		appUpdateCheckingIsPaused(state, payload) {
			state.appUpdateCheckingIsPaused = payload
		},
		decks(state, payload) {
			state.decks = payload
		},
		deleteDeckProperties(state, payload) {
			const deck = state.activeDeck

			if (!deck) return

			if (Array.isArray(payload.main)) {
				payload.main.forEach((prop) => {
					delete deck[prop]
				})
			}
			if (Array.isArray(payload.sideboard)) {
				payload.sideboard.forEach((prop) => {
					delete deck.sideboard[prop]
				})
			}
		},
		dialogData(state, payload) {
			state.dialogData = payload
		},
		focusCardBar(state, payload) {
			state.focusCardBar = payload
		},
		highlightedCardLIIndex(state, payload) {
			state.highlightedCardLIIndex = payload
		},
		holdingShiftKeyInEditor(state, payload) {
			state.holdingShiftKeyInEditor = payload
		},
		idOfShowingDialog(state, payload) {
			if (payload && payload.id !== undefined && payload.data !== undefined) {
				state.idOfShowingDialog = payload.id
				state.dialogData = payload.data
			} else {
				state.idOfShowingDialog = payload
			}
		},
		listEntryData(state, payload) {
			Object.assign(state.listEntryData, payload)
		},
		loadDefaultDecks(state, payload) {
			state.loadDefaultDecks = payload
		},
		movedAllGroupActionCount(state, payload) {
			if (typeof payload === 'number') {
				state.movedAllGroupActionCount = payload
			} else {
				state.movedAllGroupActionCount += 1
			}
		},
		mutateActiveDeck(state, payload) {
			const deck = state.activeDeck

			if (!deck) return

			Object.assign(deck, payload)
		},
		mutateDeck(state, payload) {
			const deck = state.decks.find((d) => d.name === payload.name)

			if (!deck) return

			Object.assign(deck, payload.newDeckData)
		},
		mutateCard(state, { card, newCardData, deleteProp }) {
			if (!card) return

			if (deleteProp) {
				delete card[deleteProp]
			}
			if (newCardData) {
				Object.assign(card, newCardData)
			}
		},
		mutateListEntryData(state, { category, value, extraDataString }) {
			if (!category || value === undefined) return

			const led = state.listEntryData

			if (typeof value === 'boolean') {
				led[category] = value
			} else if (category === 'invalidLines') {
				led.invalidLines.push(value)
			} else {
				Object.keys(value).forEach((groupName) => {
					if (led[category] && led[category][groupName]) {
						led[category][groupName].push(value[groupName])
					}
				})
			}

			if (extraDataString) {
				led[category].extraDataString = extraDataString
			}
		},
		/**
		 * Update the currently viewed card in the specified card group.
		 * @param {Object} state
		 * @param {string} payload.cardName
		 * @param {Object} payload.group
		 * @param {Boolean} payload.isStarred - Whether the card name AFFECTS the `viewedStarredCardName` property (rather than whether the card is starred or not).
		 * @param {Boolean} payload.doNotShow
		 * @param {Boolean} payload.removeViewedStarred - Whether to clear the `viewedStarredCardName` property regardless of the other parameters.
		 */
		mutateViewedCard(
			state,
			{ cardName, group, isStarred, doNotShow, removeViewedStarred },
		) {
			if (!group) {
				group = state.showSideboard
					? state.activeDeck.sideboard
					: state.activeDeck
			}
			if (!doNotShow) {
				group.viewedCardName = cardName
			}
			if (isStarred) {
				group.viewedStarredCardName = cardName
			}
			if (!cardName || removeViewedStarred) {
				group.viewedStarredCardName = ''
			}
			if (
				!isStarred &&
				group.viewedStarredCardName &&
				!group.cards.some((c) => c.name === group.viewedStarredCardName)
			) {
				group.viewedStarredCardName = ''
			}
		},
		numUserVisitsToDonatePage(state) {
			state.numUserVisitsToDonatePage++
		},
		overlayHoverEnabled(state, payload) {
			state.overlayHoverEnabled = payload
		},
		permanentlyHiddenNotifications(state, payload) {
			Object.keys(payload).forEach((key) => {
				if (payload[key] === true) {
					if (state.permanentlyHiddenNotifications.indexOf(key) === -1) {
						state.permanentlyHiddenNotifications.push(key)
					}
				} else {
					state.permanentlyHiddenNotifications =
						state.permanentlyHiddenNotifications.filter((n) => n !== key)
				}
			})
		},
		removeCardFromGroup(state, { group, cardIndex }) {
			if (!group || cardIndex === undefined) return

			group.cards.splice(cardIndex, 1)

			const cards = group.cards
			let newViewedCardName = null

			if (cards.length > 0) {
				if (cardIndex >= cards.length) {
					newViewedCardName = cards[cards.length - 1].name
				} else {
					newViewedCardName = cards[cardIndex].name
				}
			}

			group.viewedCardName = newViewedCardName
		},
		resetListSorting(state) {
			const deck = state.activeDeck

			if (!deck) return

			deck.cards.forEach((card) => {
				delete card.gapAfter
			})
			deck.sideboard.cards.forEach((card) => {
				delete card.gapAfter
			})

			deck.sortBy = '(Unsorted)'
		},
		showingAnyPopup(state, payload) {
			state.showingAnyPopup = payload
		},
		showCard(state, payload) {
			state.showCard = payload
		},
		showDeckMenu(state, payload) {
			state.showDeckMenu = payload
		},
		showSideboard(state, payload) {
			state.showSideboard = payload
		},
		temporarilyHiddenNotifications(state, payload) {
			const thn = state.temporarilyHiddenNotifications

			Object.keys(payload).forEach((key) => {
				if (payload[key] === true) {
					if (thn.indexOf(key) === -1) {
						thn.push(key)
					}
				} else {
					state.temporarilyHiddenNotifications = thn.filter((n) => n !== key)
				}
			})
		},
		timeAppLastModified(state, payload) {
			state.timeAppLastModified = payload
		},
		updateJustInstalled(state, payload) {
			state.updateJustInstalled = payload
		},
		viewedDrawnCardName(state, payload) {
			state.viewedDrawnCardName = payload
		},
		waitingToApplyUpdate(state, payload) {
			state.waitingToApplyUpdate = payload
		},
		___PERSIST_TOUCH(state, ts) {
			state.timeAppLastModified = ts
		},
		___APPLY_SNAPSHOT(state, snap) {
			// Accept both new and legacy shapes.
			// Legacy: { decks, loadDefaultDecks, timeAppLastModified, updateJustInstalled }
			const legacy = !snap.data
			const data = (() => {
				if (legacy) {
					return {
						decks: snap.decks || [],
						activeDeckName: null,
						loadDefaultDecks:
							typeof snap.loadDefaultDecks === 'boolean'
								? snap.loadDefaultDecks
								: state.loadDefaultDecks,
						permanentlyHiddenNotifications:
							snap.permanentlyHiddenNotifications || [],
						updateJustInstalled: snap.updateJustInstalled,
					}
				} else {
					return snap.data
				}
			})()

			state.decks = Array.isArray(data.decks) ? data.decks : []

			// Optional: revive date-ish strings if your app relies on Date objects.
			state.decks.forEach((d) => {
				if (
					d &&
					typeof d.editDate === 'string' &&
					!isNaN(Date.parse(d.editDate))
				) {
					d.editDate = new Date(d.editDate)
				}

				if (
					d &&
					typeof d.openDate === 'string' &&
					!isNaN(Date.parse(d.openDate))
				) {
					d.openDate = new Date(d.openDate)
				}

				if (d?.cards) {
					d.cards.forEach((c) => {
						if (
							c &&
							typeof c.addedDate === 'string' &&
							!isNaN(Date.parse(c.addedDate))
						) {
							c.addedDate = new Date(c.addedDate)
						}
					})
				}

				if (d?.sideboard?.cards) {
					d.sideboard.cards.forEach((c) => {
						if (
							c &&
							typeof c.addedDate === 'string' &&
							!isNaN(Date.parse(c.addedDate))
						) {
							c.addedDate = new Date(c.addedDate)
						}
					})
				}
			})

			if (state.activeDeck) {
				const found = state.decks.find((d) => d.path === state.activeDeck.path)

				state.activeDeck = found || null
			}

			if (legacy) {
				// If legacy snapshot removed the previous active deck.
				if (
					state.activeDeck &&
					!state.decks.some((d) => d.name === state.activeDeck.name)
				) {
					state.activeDeck = null
				}
			}

			if (Object.prototype.hasOwnProperty.call(data, 'loadDefaultDecks')) {
				state.loadDefaultDecks = data.loadDefaultDecks
			}

			if (
				Object.prototype.hasOwnProperty.call(data, 'numUserVisitsToDonatePage')
			) {
				state.numUserVisitsToDonatePage = data.numUserVisitsToDonatePage
			}

			if (
				Object.prototype.hasOwnProperty.call(
					data,
					'permanentlyHiddenNotifications',
				)
			) {
				state.permanentlyHiddenNotifications = Array.isArray(
					data.permanentlyHiddenNotifications,
				)
					? [...data.permanentlyHiddenNotifications]
					: []
			}

			if (Object.prototype.hasOwnProperty.call(data, 'updateJustInstalled')) {
				state.updateJustInstalled = data.updateJustInstalled
			}

			if (
				typeof snap.timeAppLastModified === 'number' &&
				snap.timeAppLastModified > state.timeAppLastModified
			) {
				state.timeAppLastModified = snap.timeAppLastModified
			}
		},
	},
	actions: {},
	plugins: [dataPersistAndSync],
})

export default store
